import { AlignmentRules } from '../alignmentRules';
import { Block } from '../block';
import { Bounding } from '../../../core/models/bounding';
import { Element } from '../../../equipment/models/elements/element';
import { EquipmentBase } from '../equipment-base';
import { Extension } from '../../../equipment/models/elements/extension';
import { Figure } from '../../../equipment/models/figures/figure';
import { GlobalSettings } from '../../../core/models/globalSettings';
import { ICanPutProductsResult } from '../canPutProductsResult';
import { Point } from '../../../core/models/point';
import { ProductErrors, ProductGroup } from '../product-group';
import { ProductGroupRotation } from '../product-group-rotation';

import { Utils } from '../../../core/services/utils.service';

export class HangBarElement extends Element {

	get anchorYCoordinate(): number {
		return Utils.round(this.getBoundingY().min - 0.08);
	}

	get anchorZCoordinate(): number {
		return this.shapes[0].begin.z;
	}

	get layoutBoundingY(): Bounding {
		return { min: this.anchorYCoordinate, max: this.anchorYCoordinate };
	}

	get layoutBoundingZ(): Bounding {
		return { min: this.shapes[0].begin.z, max: this.shapes[0].begin.z };
	}

	get linearMeters(): number {
		return this.Width;
	}

	get products(): Array<Array<ProductGroup>> {
		const reverse: number = this.stand.alignment === 'right' ? -1 : 1;
		const products: Array<ProductGroup> = super.sortedProducts;

		return products
			.map((pg: ProductGroup) => [pg])
			.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => reverse * (a[0].begin.x - b[0].begin.x));
	}

	get sortedProducts(): Array<ProductGroup> {
		return super.sortedProducts.sort((a: ProductGroup, b: ProductGroup) => a.begin.x - b.begin.x);
	}

	constructor(element: Element) {
		super(element.parent, element.shapes, element.extensions, element.depend, element.id, element.idx);
	}

	public addProduct(pg: ProductGroup) {
		pg.element = this;
		pg.placementDepth = 1;
		pg.placementHeight = 1;
		pg.rotation = ProductGroupRotation.Side;
		pg.angle = GlobalSettings.DEFAULT_PRODUCT_GROUP_ANGLE;
		pg.image.rotation = 0;
		pg.begin = {
			x: pg.begin.x,
			y: Utils.round(this.anchorYCoordinate - pg.ItemHeight),
			z: Utils.round(this.anchorZCoordinate + pg.ItemDepth / 2)
		};
	}

	canDrop(product: ProductGroup): boolean {
		return !product.isLooseForm;
	}

	copy(): HangBarElement {
		const shapes: Array<Figure> = this.shapes.map((shape: Figure) => shape.copy()),
			extensions: Array<Extension<any>> = this.extensions.map((extension: Extension<any>) => extension.copy()),
			element: Element = new Element(this.parent, shapes, extensions, this.depend, this.id, this.idx);

		return new HangBarElement(element);
	}

	normalize(stand: EquipmentBase): void {
		const zGroups: Array<Array<ProductGroup>> = this.products;
		const bounding: Bounding = this.layoutBoundingX;

		if (stand.alignment === 'right') {
			zGroups.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => {
				return Element.calculateGroupMax(b) - Element.calculateGroupMax(a);
			});
		} else {
			zGroups.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => {
				return Element.calculateGroupMin(a) - Element.calculateGroupMin(b);
			});
		}

		zGroups.forEach((group: Array<ProductGroup>, idx: number) => {
			const align: AlignmentRules = Element.alignmentRules(zGroups, idx, bounding);
			const beginPoint: number = !idx ? align[stand.alignment].startPoint() : align[stand.alignment].nextPoint();

			group.forEach((pg: ProductGroup) => {
				if (pg.dependProduct) pg.dependProduct = null;
				pg.begin.x = isFinite(beginPoint) ? beginPoint : align[stand.alignment].startPoint();
				pg.begin.y = Utils.round(this.anchorYCoordinate - pg.ItemHeight);
				pg.begin.z = Utils.round(this.anchorZCoordinate + pg.ItemDepth / 2);

				pg.error = false;

				const errors: { [key in ProductErrors]?: boolean } = {
					[ProductErrors.HangBarLeftLimit]: pg.getBoundingX().min < bounding.min,
					[ProductErrors.HangBarRightLimit]: pg.getBoundingX().max > bounding.max,
					[ProductErrors.LooseOnShelf]: pg.isLooseForm
				};

				pg.error = Object.values(errors).some((error: boolean) => error);
				pg.errorTypes = Object.keys(errors).filter((key: ProductErrors) => errors[key]);
			});
		});
	}

	public canMoveProductGroup(productGroup: ProductGroup, x?: number, y?: number, z?: number): boolean {
		let canMove: boolean = false;
		const elementBoundingX: Bounding = this.layoutBoundingX;
		const productBoundingX: Bounding = productGroup.getBoundingX();

		if (x && x < 0 && elementBoundingX.min <= Utils.round(productBoundingX.min + x)) canMove = true;
		if (x && x > 0 && elementBoundingX.max >= Utils.round(productBoundingX.max + x)) canMove = true;

		return canMove;
	}

	public canPutProductGroups(products: Array<ProductGroup>, point: Point): ICanPutProductsResult {
		if (products.some((pg: ProductGroup) => pg.isLooseForm)) {
			return {
				success: false,
				message: {
					messageText: 'WEIGHT_PRODUCT_ON_SHELF_ERROR',
					translateParams: {}
				}
			};
		}

		const block: Block = this.stand.findBlock(point.x, point.y, products[0].Width);

		for (let product of products) {
			if (block && (product.block != block.id || !block.id)) {
				if (!block.isProductSuitable(product.product)) {
					return {
						success: false,
						message: {
							messageText: 'BLOCK_PRODUCT_ERROR',
							translateParams: { blockName: block.name }
						}
					};
				}
			}
		}

		return {
			success: true,
			message: {
				messageText: '',
				translateParams: {}
			}
		}
	}

	public putProductGroups(products: Array<ProductGroup>, list: Array<Array<ProductGroup>>, point: Point): void {
		products.forEach((pg: ProductGroup) => {
			const faces: number = pg.FacesQty;
			const centerX: number = Utils.round(point.x + pg.Width / 2);

			pg.dependProduct = null;

			if (pg.element != this) {
				pg.Element = this;
				pg.begin.x = Utils.round(centerX - pg.Width / 2);
				pg.FacesQty = faces;
			} else {
				pg.begin.x = Utils.round(centerX - pg.Width / 2);
			}

			this.stand.moveProductToTheEnd(pg);
		});
	}

	public shiftProductGroups(activeGroups: Array<Array<ProductGroup>>, excludeProducts: Array<ProductGroup> = []): void {
		const startPoint: number = Utils.min(activeGroups.map((group: Array<ProductGroup>) => group[0].begin.x));
		const endPoint: number = Utils.max(activeGroups.map((group: Array<ProductGroup>) => Element.calculateGroupMax(group)));
		const bounding: Bounding = this.layoutBoundingX;
		const products: Array<Array<ProductGroup>> = this.products
				.map((group: Array<ProductGroup>) => group.filter((pg: ProductGroup) => !excludeProducts.includes(pg)))
				.filter((group: Array<ProductGroup>) => group.length),
			leftProducts: Array<Array<ProductGroup>> = products
				.filter((group: Array<ProductGroup>) => group[0].begin.x < startPoint && group[0].begin.x >= bounding.min)
				.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => b[0].begin.x - a[0].begin.x),
			rightProducts: Array<Array<ProductGroup>> = products
				.filter((group: Array<ProductGroup>) => group[0].begin.x >= startPoint && group[0].begin.x < bounding.max)
				.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => a[0].begin.x - b[0].begin.x);

		this.shiftLeft(startPoint, leftProducts);
		this.shiftRight(endPoint, rightProducts);

		if (this.stand.alignment === 'right') {
			const productsToShift: Array<Array<ProductGroup>> = [...leftProducts, ...activeGroups, ...rightProducts]
				.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => a[0].begin.x - b[0].begin.x);
			this.shiftRight(bounding.min, productsToShift);
			const productsToShiftLeft: Array<Array<ProductGroup>> = [
				...productsToShift
					.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => b[0].begin.x - a[0].begin.x),
				...products
					.filter((group: Array<ProductGroup>) => {
						return group[0].begin.x < bounding.min && !leftProducts.some((pg: Array<ProductGroup>) => pg.includes(group[0]));
					})
					.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => b[0].begin.x - a[0].begin.x)
			];
			this.shiftLeft(bounding.max, productsToShiftLeft);
		} else {
			const productsToShift: Array<Array<ProductGroup>> = [...rightProducts, ...activeGroups, ...leftProducts]
				.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => b[0].begin.x - a[0].begin.x);
			this.shiftLeft(bounding.max, productsToShift);
			const productsToShiftRight: Array<Array<ProductGroup>> = [
				...productsToShift
					.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => a[0].begin.x - b[0].begin.x),
				...products
					.filter((group: Array<ProductGroup>) => {
						const groupMax: number = Element.calculateGroupMax(group);
						return groupMax > bounding.max && !rightProducts.some((pg: Array<ProductGroup>) => pg.includes(group[0]));
					})
					.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => a[0].begin.x - b[0].begin.x)
			];
			this.shiftRight(bounding.min, productsToShiftRight);
		}
	}

	private shiftLeft(startPoint: number, groups: Array<Array<ProductGroup>>): void {
		let index: number = 0;
		while (index < groups.length && Element.calculateGroupMax(groups[index]) > startPoint) {
			startPoint = Utils.round(startPoint - Element.calculateGroupWidth(groups[index]));
			groups[index].forEach((pg: ProductGroup) => pg.begin.x = startPoint);
			index++;
		}
	};

	private shiftRight(endPoint: number, groups: Array<Array<ProductGroup>>): void {
		let index: number = 0;
		while (index < groups.length && Element.calculateGroupMin(groups[index]) < endPoint) {
			groups[index].forEach((pg: ProductGroup) => pg.begin.x = endPoint);
			endPoint = Element.calculateGroupMax(groups[index]);
			index++;
		}
	};
}